/**
 * BrokerRevolver - Animated rotating broker logo carousel
 *
 * Displays supported broker logos one at a time with a 3D rotation animation.
 * Auto-rotates through brokers on an interval.
 *
 * @example
 * ```tsx
 * <BrokerRevolver interval={2500} />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BROKER_LOGOS } from '@/shared/components/full-site/constants/heroConstants';

interface BrokerRevolverProps {
  /** Additional CSS classes */
  className?: string;
  /** Rotation interval in milliseconds (default: 2500) */
  interval?: number;
  /** Hide the section label */
  hideLabel?: boolean;
}

export const BrokerRevolver: React.FC<BrokerRevolverProps> = ({
  className = '',
  interval = 2500,
  hideLabel = false,
}) => {
  const { t } = useTranslation();
  const [brokerIndex, setBrokerIndex] = useState(0);

  // Auto-rotate through brokers
  useEffect(() => {
    const timer = setInterval(() => {
      setBrokerIndex((prev) => (prev + 1) % BROKER_LOGOS.length);
    }, interval);
    return () => clearInterval(timer);
  }, [interval]);

  return (
    <div className={`w-full flex flex-col items-center ${className}`}>
      {!hideLabel && (
        <div className="section-label">
          {t('hero.heroAIChat.supportedBrokers', 'Supported Brokers')}
        </div>
      )}
      <div className="hero-logo-revolver">
        <div className="hero-logo-single">
          {BROKER_LOGOS.map((broker, index) => (
            <div
              key={broker.short}
              className={`hero-logo-pill with-image ${index === brokerIndex ? 'active' : ''}`}
              style={{
                opacity: index === brokerIndex ? 1 : 0,
                transform: index === brokerIndex ? 'rotateY(0deg)' : 'rotateY(90deg)',
                transition: 'opacity 0.3s ease-in-out, transform 0.6s ease-in-out',
              }}
            >
              <span className="hero-logo-image-wrapper">
                <img
                  src={broker.logo}
                  alt={`${broker.name} logo`}
                  className={`hero-logo-image hero-logo-${broker.short.toLowerCase()}`}
                  width={120}
                  height={20}
                  style={{
                    width: '120px',
                    height: '20px',
                    maxWidth: '120px',
                    maxHeight: '20px',
                    objectFit: 'contain',
                  }}
                />
              </span>
              {/* Special case: Show name for Charles Schwab */}
              {broker.short === 'SCHW' && (
                <span className="hero-logo-name">Charles Schwab</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrokerRevolver;
